import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";

const POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;

const BillingSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", attempts: 0, plan: null, error: null });
  const stoppedRef = useRef(false);

  const poll = useCallback(async (attempt) => {
    if (stoppedRef.current) return;
    if (!sessionId) {
      setState({ status: "error", error: "session_id ausente na URL" });
      return;
    }
    try {
      const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState({ status: "paid", plan: data.plan, attempts: attempt });
        return;
      }
      if (data.status === "expired") {
        setState({ status: "expired", attempts: attempt });
        return;
      }
      if (attempt >= POLL_ATTEMPTS) {
        setState({ status: "timeout", attempts: attempt });
        return;
      }
      setState({ status: "checking", attempts: attempt });
      setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
    } catch (err) {
      setState({ status: "error", error: formatApiError(err) });
    }
  }, [sessionId]);

  useEffect(() => {
    poll(1);
    return () => {
      stoppedRef.current = true;
    };
  }, [poll]);

  const renderContent = () => {
    switch (state.status) {
      case "paid":
        return (
          <>
            <CheckCircle2 className="w-16 h-16 text-foreground mb-6" strokeWidth={1.5} />
            <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">Pagamento Aprovado</div>
            <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter mb-6">
              Tudo certo!
            </h1>
            <p className="text-base text-foreground/70 max-w-md mb-8">
              Sua assinatura{state.plan ? ` do plano ${state.plan}` : ""} foi ativada. 30 dias adicionados ao seu acesso.
            </p>
          </>
        );
      case "checking":
        return (
          <>
            <Loader2 className="w-12 h-12 animate-spin mb-6" />
            <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">Aguardando confirmação</div>
            <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mb-2">Processando…</h1>
            <p className="text-sm text-foreground/60">Tentativa {state.attempts} de {POLL_ATTEMPTS}</p>
          </>
        );
      case "expired":
        return (
          <>
            <AlertCircle className="w-12 h-12 text-destructive mb-6" />
            <h1 className="font-heading text-4xl font-black tracking-tighter mb-4">Sessão expirada</h1>
            <p className="text-sm text-foreground/70 mb-6 max-w-md">
              A sessão de pagamento expirou antes da conclusão. Tente novamente no painel de assinatura.
            </p>
          </>
        );
      case "timeout":
        return (
          <>
            <AlertCircle className="w-12 h-12 text-foreground mb-6" />
            <h1 className="font-heading text-4xl font-black tracking-tighter mb-4">Verificação demorou</h1>
            <p className="text-sm text-foreground/70 mb-6 max-w-md">
              Não conseguimos confirmar o pagamento ainda. Volte ao painel — se foi cobrado, em alguns
              minutos sua assinatura será atualizada automaticamente.
            </p>
          </>
        );
      default:
        return (
          <>
            <AlertCircle className="w-12 h-12 text-destructive mb-6" />
            <h1 className="font-heading text-4xl font-black tracking-tighter mb-4">Erro</h1>
            <p className="text-sm text-destructive mb-6 max-w-md">{state.error}</p>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-2xl w-full flex flex-col items-center text-center" data-testid="billing-success-page">
        {renderContent()}
        <Link to="/admin">
          <Button className="rounded-none h-12 px-8 uppercase tracking-[0.2em]" data-testid="billing-success-back">
            Voltar ao Admin <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default BillingSuccess;
